<template>
    <ui-modal>
        <template #header>
            <h3 class="mb-1 text-center" v-text="'Świetnie!'" />
        </template>

        <div class="text-center mb-3">
            <a-icon icon="Ok" width="40px" height="40px" />
        </div>

        <h5
            v-html="`Zakończyłeś/aś proces wyboru sklepów oraz uzupełniania danych niezbędnych
            do integracji z operatorem.<br/>
            Jeśli dla wybranych sklepów jest to pierwsza integracja, operator ${operator.name}
            zostanie w nich ustawiony jako preferowany dla wszystkich frakcji.<br/>
            Od teraz będziemy kierowali do niego dane dotyczące Twojej zbiórki w tych sklepach.`"
            class="text-primary text-center font-weight-semi"
        />

        <page-preloader v-if="loaderLocations" min-height="100px" />
        <div v-else-if="locations.length > 0" class="c-table contract-locations-summary">
            <div class="c-thead">
                <div class="c-td">Sklep</div>
                <div class="c-td">Przypisanie sklepu do umowy</div>
                <div class="c-td">Zapisanie numeru punktu zbiórki</div>
                <div class="c-td">Preferowany operator</div>
            </div>
            <div class="c-tbody">
                <div v-for="location in locations" :key="location.id" class="c-tr">
                    <div class="c-td">
                        {{ location.full_name }}<br />
                        ul. {{ location.fullAddress }}
                    </div>
                    <div class="c-td">
                        <span
                            v-text="'Przypisanie sklepu do umowy'"
                            class="d-flex d-md-none"
                        />
                        <a-icon
                            v-tooltip="location.selected
                                ? 'Przypisanie sklepu do umowy zakończyło się sukcesem'
                                : `Przypisanie sklepu do umowy nie powiodło się.
                                    Wejdź w tryb edycji umowy i spróbuj ponownie`"
                            :icon="location.selected ? 'Ok' : 'ErrorCircle'"
                        />
                    </div>
                    <div class="c-td">
                        <span
                            v-text="'Zapisanie numeru punktu zbiórki'"
                            class="d-flex d-md-none"
                        />
                        <a-icon
                            v-tooltip="!!location.collectionPointId
                                ? 'Numer punktu zbiórki został uzupełniony'
                                : 'Wejdź w tryb edycji i uzupełnij numer punktu zbiórki dla tego sklepu'"
                            :icon="!!location.collectionPointId ? 'Ok' : 'ErrorCircle'"
                        />
                    </div>
                    <div class="c-td">
                        <span
                            v-text="'Preferowany operator'"
                            class="d-flex d-md-none"
                        />
                        {{ location.preferredOperator || '---' }}
                    </div>
                </div>
            </div>
        </div>

        <template #footer>
            <the-btn
                text="Zamknij"
                class="btn-outline-custom"
                @click="$emit('close-modal')"
            />
            <the-btn
                text="Przejdź do pulpitu systemu kaucyjnego"
                class="btn-secondary"
                @click="goToDrs"
            />
        </template>
    </ui-modal>
</template>

<script>
import { mapGetters } from 'vuex'
import { FRACTIONS, FRACTIONS_TYPES } from '@/utils/constants/drs/fractions'
import { CONTRACT_MODES } from '@/components/drs/operators/-utils'

const PLASTIC_FRACTION_ID = FRACTIONS[FRACTIONS_TYPES.PLASTIC].id

export default {
    data: () => ({
        contractDetails: [],
        locations: [],
        loaderLocations: false
    }),

    computed: {
        ...mapGetters('drsOperators', ['getServiceByOperatorId']),

        modalArgs() {
            return this.$store.getters.modalState.args
        },

        isEditMode() {
            return this.modalArgs.mode === CONTRACT_MODES.EDIT
        },

        operator() {
            return this.modalArgs.operator
        },

        contract() {
            return this.modalArgs.contract
        }
    },

    methods: {
        async goToDrs() {
            this.$store.dispatch('toggleModal', false)
            this.$router.push({ name: 'drs' })
        },

        isAssignedToContract(location) {
            return location.contract_id === this.contract.id
        },

        async loadLocations() {
            this.loaderLocations = true

            this.locations = await Promise.all(this.modalArgs.locations.map(async location => {
                const { data, status } = await this.$store.$api.get(
                    `drs/api/proxy/entity/location/configuration/${location.locationId}`,
                    { config: { noCancel: true } }
                )
                if (status !== 200) {
                    return location
                }
                const fractions = data?.fractions || []
                const plasticFraction = fractions.find(fr => fr.is_active && fr.id === PLASTIC_FRACTION_ID)
                const preferredOperatorId = plasticFraction ? plasticFraction.dict_operator_id : null
                const preferredOperator = preferredOperatorId ? this.getServiceByOperatorId(preferredOperatorId)?.name : '---'

                return {
                    ...location,
                    preferredOperator
                }
            }))

            this.loaderLocations = false
        }
    },

    created() {
        this.$store.$api.get(`drs/api/operator_contracts/${this.contract.id}`)
            .then(response => {
                this.contractDetails = response.data.contractDetails || []
            })
            .catch(err => {
                console.error('Error fetching contract locations:', err)
            })

        this.loadLocations()
    }
}
</script>

<style lang="scss">
@import '../../../styles/_custom.scss';
@import '~bootstrap/scss/mixins';

.contract-locations-summary {
    .c-thead {
        text-align: center;
    }
    .c-tbody {
        text-align: center;
    }
    .c-tr {
        border: 1px solid var(--color-border);
        border-radius: 4px;
        flex-direction: row;

        +.c-tr {
            margin-top: 1rem;
        }
    }
    .c-td {
        display: flex;
        align-self: center;
        justify-content: center;

        &:nth-child(1) {
            --width: 60%;
            justify-content: start;
            text-align: left;
        }
        &:nth-child(2) {
            --width: 10%;
        }
        &:nth-child(3) {
            --width: 10%;
        }
        &:nth-child(4) {
            --width: 10%;
        }
    }

    @include media-breakpoint-down(sm) {
        margin-top: 1.5rem;
        .c-tr {
            flex-direction: column;
            padding-top: 0.75rem;
            padding-bottom: 0.75rem;
        }
        .c-td {
            --width: 100%;
            justify-content: center;
            gap: 1rem;
            align-items: center;
            padding-top: 0.25rem;
            padding-bottom: 0.25rem;

            &:nth-child(1) {
                --width: 100%;
                justify-content: center;
                text-align: center;
                font-weight: 700;
            }
            &:nth-child(2) {
                --width: 100%;
            }
            &:nth-child(3) {
                --width: 100%;
            }
            &:nth-child(4) {
                --width: 100%;
            }
        }
    }
}
</style>